<template>
  <b-row>
    <b-col cols="12">
      <b-row class="justify-content-between mb-3">
        <b-col cols="3" sm="6" md="3">
          <label>Rechercher</label>
          <b-form-input v-model="search" placeholder="Tapez pour rechercher" debounce="500" />
        </b-col>
        <b-col cols="3" sm="6" md="3">
          <label>par page</label>
          <b-select v-model="perPage" :options="[10, 20, 50, 100]"></b-select>
        </b-col>
      </b-row>
    </b-col>
    <b-col cols="12">
      <div
        v-if="isPositionChangeing"
        class="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
      >
        <font-awesome-icon class="fa-3x" icon="spinner" pulse />
      </div>
      <b-table
        id="mt-table"
        ref="mr-table"
        bordered
        class="agv-table td-middle"
        :current-page="currentPage"
        :per-page="perPage"
        :items="tableData"
        :primary-key="'id'"
        :fields="fields"
        :filter="search"
        :filter-included-fields="filter"
        :busy.sync="isBusy"
        sort-by="position"
        :class="isPositionChangeing ? 'opacity-on' : ''"
        show-empty
        @filtered="onFiltered"
      >
        <template v-slot:cell(position)="row">
          <div :style="`max-width: 80px;`">
            <appInput
              :ref="'position-' + row.item.id"
              :text="row.item.position"
              :property="'position'"
              :input-type="'number'"
              :item-id="row.item.id"
              :max-input="tableData.length"
              @update="updatePosition"
            ></appInput>
          </div>
        </template>
        <template v-slot:cell(description)="row">
          <!-- eslint-disable vue/no-v-html -->
          <div style="max-height: 70px; overflow: auto;" v-html="row.item.description"></div>
        </template>
        <template v-slot:cell(majority)="row">
          <appVSelect
            v-if="row.item.children_resolutions.length == 0 && majorities.length > 0"
            :ref="'majority-' + row.item.id"
            :item-id="row.item.id"
            :property="'majority_id'"
            :selected-value="row.item.majority_id"
            :options="majorities"
            :clear="false"
            @input="updateProperty"
          />
        </template>
        <template v-slot:cell(voting_category)="row">
          <appVSelect
            v-if="
              row.item.children_resolutions.length == 0 &&
              row.item.majority_id != defaultSansVoteMajorityId &&
              sortedVotingCategories.length > 0
            "
            :ref="'voting-category-' + row.item.id"
            :item-id="row.item.id"
            :property="'voting_category_id'"
            :selected-value="row.item.voting_category_id"
            :options="sortedVotingCategories"
            :clear="false"
            @input="updateProperty"
          />
        </template>
        <template #row-details="data">
          <b-card>
            <b-row class="justify-content-md-center">
              <b-col cols="10">
                <b-list-group
                  v-for="ch_resolution in sortedSubResolutionByPosition(
                    data.item.children_resolutions
                  )"
                  :key="ch_resolution.id"
                >
                  <b-list-group-item class="d-flex mb-2">
                    <div>
                      <span>
                        {{ ch_resolution.position }}
                      </span>
                    </div>
                    <div class="d-flex flex-column align-items-start ml-4">
                      <span>
                        <strong>Nom :</strong>
                        {{ ch_resolution.name }}
                      </span>
                      <span v-if="ch_resolution.description" class="d-flex">
                        <strong>Description :</strong>
                        <span v-html="ch_resolution.description"></span>
                      </span>
                      <span v-if="ch_resolution.majority_id">
                        <strong>Majorité :</strong>
                        {{ getMajorityName(ch_resolution.majority_id) }}
                      </span>
                      <span
                        v-if="
                          ch_resolution.voting_category_id &&
                          ch_resolution.majority_id != defaultSansVoteMajorityId
                        "
                      >
                        <strong>Catégorie de vote :</strong>
                        {{ getVotingCategoryName(ch_resolution.voting_category_id) }}
                      </span>
                    </div>
                  </b-list-group-item>
                </b-list-group>
              </b-col>
            </b-row>
          </b-card>
        </template>
        <template v-slot:cell(_actions)="row">
          <b-row class="actions text-center" style="min-width: 150px;">
            <b-col>
              <b-button
                v-if="row.item.children_resolutions.length > 0"
                size="sm"
                class="btn btn-edit tablebutton mr-3"
                :title="'Sommaire résolution'"
                @click="row.toggleDetails"
              >
                <font-awesome-icon icon="info-circle" size="1x" />
              </b-button>
              <b-button
                size="sm"
                :title="$t('general.edit')"
                class="tablebutton btn-edit"
                @click="EDIT(row.item)"
              >
                <font-awesome-icon icon="pen" size="1x" />
              </b-button>
              <b-button class="tablebutton btn-red">
                <b-button
                  size="sm"
                  :title="$t('general.delete')"
                  class="tablebutton btn-red"
                  @click="DELETE_RESOLUTION(row.item)"
                >
                  <font-awesome-icon icon="trash-alt" size="1x" />
                </b-button>
              </b-button>
            </b-col>
          </b-row>
        </template>
        <template #empty>
          <span> {{ "Aucune donnée n'a été trouvée..." }}</span>
        </template>
      </b-table>
    </b-col>
    <b-col cols="12" class="text-center">
      <b-col cols="4" class="m-auto">
        <b-pagination
          v-if="totalRows / perPage > 1"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          aria-controls="mt-table"
        ></b-pagination>
      </b-col>
    </b-col>
  </b-row>
</template>

<script>
import MeetingResolutionMW from "@/middlewares/MeetingResolution";
import MeetingResolutionORM from "@/store/orm-axios/models/MeetingResolution";
import { defaultSansVoteMajorityId } from "@/constants/default.js";
export default {
  components: {
    appTextArea: () => import("../../components/b-table-components/TextArea"),
    appVSelect: () => import("../../components/b-table-components/VSelect"),
    appInput: () => import("../../components/b-table-components/Input"),
  },
  props: {
    items: {
      type: Array,
      default: () => {
        return [];
      },
    },
    votingCategories: {
      type: Array,
      default: () => {
        return [];
      },
    },
    majorities: {
      type: Array,
      default: () => {
        return [];
      },
    },
    meetingId: {
      type: String,
      default: null,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    tableCurrentPage: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      defaultSansVoteMajorityId,
      perPage: 10,
      currentPage: 1,
      isBusy: false,
      search: "",
      filter: ["name", "description", "voting_category", "majority"],
      tableData: [],
      totalRows: 0,
      dragging: false,
      fields: [
        {
          key: "position",
          label: "Positionner",
          sortable: true,
        },
        { key: "name", label: "Résolution", sortable: true, sortDirection: "desc" },
        { key: "description", label: "Description", sortable: true, sortDirection: "desc" },
        { key: "majority", label: "Majorité", sortable: true, sortDirection: "desc" },
        {
          key: "voting_category",
          label: "Catégorie de vote",
          sortable: true,
          sortDirection: "desc",
        },
        {
          key: "_actions",
          label: "Actions",
          sortable: false,
        },
      ],
      isPositionChangeing: false,
      tableDataForPositionUpdate: [],
    };
  },
  computed: {
    sortedVotingCategories() {
      return this.$_.sortBy(this.votingCategories, item => {
        return item.name;
      });
    },
  },
  watch: {
    items: {
      handler(newVal, oldVal) {
        this.tableData = newVal;

        this.updateTableDataPositionOnChange();

        this.totalRows = this.tableData.length;
        this.$refs["mr-table"].$forceUpdate();
      },
      deep: true,
    },
    perPage(newVal, oldVal) {
      if (this.perPage * this.currentPage > this.totalRows) {
        this.currentPage = 1;
      }
      if (newVal != oldVal) {
        this.changeItemsPerPage();
      }
    },
    currentPage(newVal, oldVal) {
      if (newVal != oldVal) {
        this.changeCurrentPage();
      }
    },
  },
  created() {
    this.perPage = this.itemsPerPage;
    this.currentPage = this.tableCurrentPage;

    this.tableData = this.items;
    this.totalRows = this.tableData.length;

    this.updateTableDataPositionOnChange();
  },
  methods: {
    DELETE_RESOLUTION($ev) {
      this.$emit("deletePressed", $ev);
    },
    EDIT($ev) {
      this.$emit("editPressed", $ev);
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    updateProperty($ev) {
      var index = this.$_.findIndex(this.tableData, { id: $ev.itemId });
      MeetingResolutionORM.update({
        where: $ev.itemId,
        data: {
          [$ev.property]: $ev.value,
        },
      });
      var mt = MeetingResolutionORM.find(this.tableData[index].id);
      MeetingResolutionMW.PUT(mt)
        .then(() => {
          this.tableData[index][$ev.property] = $ev.value;
        })
        .catch(err => {
          console.error(err);
        });
    },
    async updatePosition($ev) {
      var changedMeetingResolutionIds = await this.updatePositionInORM($ev);
      var meetingResolutionsToUpdateInDb = MeetingResolutionORM.query()
        .where(mr => {
          return changedMeetingResolutionIds.includes(mr.id);
        })
        .get();
      this.$store.state.showLoaderOverlay = true;
      if (
        this.$_.isArray(meetingResolutionsToUpdateInDb) &&
        meetingResolutionsToUpdateInDb.length > 0
      ) {
        await MeetingResolutionMW.BULK_PUT(meetingResolutionsToUpdateInDb);
      }
      this.$store.state.showLoaderOverlay = false;
      console.log("meetingResolutionsToUpdateInDb", meetingResolutionsToUpdateInDb);
      this.tableData = MeetingResolutionORM.query()
        .where(mr => {
          return mr.parent_resolution_id == null && mr.meeting_id == this.meetingId;
        })
        .withAllRecursive(1)
        .get();
      this.$emit("reloadTableData");
    },
    updatePositionInORM($ev) {
      console.log("updatePosition", $ev);
      var changedMeetingResolutionIds = [$ev.itemId];
      var newPosition = parseInt($ev.value);
      var changedMR = MeetingResolutionORM.find($ev.itemId);
      //# case 1: 5 -> 2
      if (newPosition <= changedMR.position) {
        var toIncreasePositionMR = MeetingResolutionORM.query()
          .where(mr => {
            return (
              mr.parent_resolution_id == null &&
              mr.position >= newPosition &&
              mr.position < changedMR.position
            );
          })
          .get();
        console.log("toIncreasePositionMR", toIncreasePositionMR);
        this.$_.each(toIncreasePositionMR, mr => {
          //
          MeetingResolutionORM.update({
            where: mr.id,
            data: {
              position: mr.position + 1,
            },
          });
          changedMeetingResolutionIds.push(mr.id);
        });
        MeetingResolutionORM.update({
          where: changedMR.id,
          data: {
            position: newPosition,
          },
        });
      } else {
        var toDecreasePositionMR = MeetingResolutionORM.query()
          .where(mr => {
            return (
              mr.parent_resolution_id == null &&
              mr.position >= changedMR.position &&
              mr.position <= newPosition
            );
          })
          .get();
        console.log("toDecreasePositionMR", toDecreasePositionMR);

        this.$_.each(toDecreasePositionMR, mr => {
          //
          MeetingResolutionORM.update({
            where: mr.id,
            data: {
              position: mr.position - 1,
            },
          });
          changedMeetingResolutionIds.push(mr.id);
        });
        MeetingResolutionORM.update({
          where: changedMR.id,
          data: {
            position: newPosition,
          },
        });
      }
      console.log(
        MeetingResolutionORM.query().where("parent_resolution_id", null).orderBy("position").get()
      );
      console.log("changedMeetingResolutionIds", changedMeetingResolutionIds);
      return changedMeetingResolutionIds;
    },
    checkItemPosition(item, index) {
      if (item.position != index + 1) {
        console.log("WRONG item position", item.position, index + 1);
        item.position = index + 1;
        MeetingResolutionORM.update({
          where: item.id,
          data: {
            position: item.position,
          },
        });
        this.tableDataForPositionUpdate.push({ id: item.id, position: item.position });
      }
    },
    savePosition() {
      MeetingResolutionMW.SAVE_POSITION({ meetingResolutions: this.tableDataForPositionUpdate });
    },
    getMajorityName(majorityId) {
      // NOTE: When meeting was not created yet, meetingDetails is missing so we look in the majorities
      if (this.isInMeetingUpsert) {
        const majority = this.majorities.find(item => item.id == majorityId);
        if (majority) {
          return majority.name;
        }
      }
      const majority = this.findMajorityData(majorityId);
      return majority.name;
    },
    getVotingCategoryName(votingCategoryId) {
      // NOTE: When meeting was not created yet, meetingDetails is missing so we look in the votingCategories
      if (this.isInMeetingUpsert) {
        const votingCategory = this.votingCategories.find(item => item.id == votingCategoryId);
        if (votingCategory) {
          return votingCategory.name;
        }
      }
      let votingCategory = this.findVotingCategoryData(votingCategoryId);
      return votingCategory.name;
    },
    sortedSubResolutionByPosition(children_resolutions) {
      return this.$_.sortBy(children_resolutions, item => {
        return item.position;
      });
    },
    changeItemsPerPage() {
      this.$emit("changeItemsPerPage", this.perPage);
    },
    changeCurrentPage() {
      this.$emit("changeCurrentPage", this.currentPage);
    },
    updateTableDataPositionOnChange() {
      this.$_.each(this.tableData, (item, index) => {
        item.key = index;
        // NOTE:
        //  Checking MeetingResolution position, update the position by table position and then save in
        //  tableDataForPositionUpdate to call later api update
        this.checkItemPosition(item, index);
      });
      // NOTE: Update every position that is not correct in api
      if (this.tableDataForPositionUpdate.length > 0) {
        this.savePosition();
        this.tableDataForPositionUpdate = [];
      }
    },
  },
};
</script>

<style scoped>
.table td {
  vertical-align: middle !important;
}
</style>
